import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is authenticated on mount
  useEffect(() => {
    checkAuth();
  }, []);

  /**
   * Check authentication status
   */
  const checkAuth = async () => {
    try {
      setLoading(true);
      const response = await authService.getMe();
      if (response.success && response.data?.user) {
        setUser(response.data.user);
        setError(null);
      } else {
        setUser(null);
      }
    } catch (err) {
      setUser(null);
      setError(null); // Don't show error on initial check
    } finally {
      setLoading(false);
    }
  };

  /**
   * Register new user
   */
  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await authService.register(userData);
      if (response.success && response.data?.user) {
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
        }
        setUser(response.data.user);
        return { success: true, user: response.data.user };
      }
      return { success: false, error: 'Registration failed' };
    } catch (err) {
      const errorMessage = err.message || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Login user
   */
  const login = async (credentials) => {
    try {
      setLoading(true);
      setError(null);
      const response = await authService.login(credentials);
      if (response.success && response.data?.user) {
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
        }
        setUser(response.data.user);
        return { success: true, user: response.data.user };
      }
      // If response exists but not successful, extract error message
      const errorMsg = response?.error || response?.message || 'Login failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } catch (err) {
      // Extract error message from various possible locations
      const errorMessage = err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout user
   */
  const logout = async () => {
    try {
      setLoading(true);
      await authService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setError(null);
      setLoading(false);
      localStorage.removeItem('token');
    }
  };

  /**
   * Check if user has specific role
   */
  const hasRole = (roleName) => {
    if (!user || !user.roles) return false;
    return user.roles.some((role) => role.name === roleName.toUpperCase());
  };

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = (roleNames) => {
    if (!user || !user.roles) return false;
    const userRoleNames = user.roles.map((role) => role.name);
    return roleNames.some((roleName) =>
      userRoleNames.includes(roleName.toUpperCase())
    );
  };

  /**
   * Check if user has all specified roles
   */
  const hasAllRoles = (roleNames) => {
    if (!user || !user.roles) return false;
    const userRoleNames = user.roles.map((role) => role.name);
    return roleNames.every((roleName) =>
      userRoleNames.includes(roleName.toUpperCase())
    );
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    register,
    login,
    logout,
    checkAuth,
    hasRole,
    hasAnyRole,
    hasAllRoles,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
